import React, { useEffect, useState, useRef, useContext } from "react";
import { formatDateOnly } from "helpers/dateOnly";
import { useNavigate, useParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { Badge, Box, Flex, Label, Link, Spinner } from "theme-ui";
import moment from "moment-timezone";

import { themevals } from "theme/themevals";
import { deleteClient, getClient, resyncClient, getClientOverview } from "api/Clients";
import { getQuoteRequests } from "api/QuoteRequests";
import { getQuotes } from "api/Quotes";
import { getProjects } from "api/Projects";
import { getInvoices } from "api/Invoices";
import { getPurchaseOrders } from "api/PurchaseOrder";
import { getServicePlans } from "api/ServicePlans";

import ReloadIcon from "components/svg/icons/ReloadIcon";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { BackArrowIcon, PencilIcon, QuestionMarkIcon, TrashIcon } from "components/svg";
import H2 from "components/typography/H2";
import P from "components/typography/P";
import Span from "components/typography/Span";
import BasicModal from "components/ui/BasicModal";
import BasicButton from "components/forms/BasicButton";
import TableData from "components/ui/TableData";
import { Tab, Tabs } from "components/ui/Tabs";
import { useToast } from "components/toast";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";
import InvoiceStatusBadge from "components/ui/InvoiceStatusBadge";

import ContactsTab from "./components/ContactsTab";
import OverviewTab from "./components/OverviewTab";
import PlansTab from "./components/PlansTab";
import NotesTab from "./components/NotesTab";
import TabSection from "./components/TabSection";

const getClientInvoices = ({ query = {}, controller = null, excludeInterceptor = false }: any) =>
  getInvoices({
    query: {
      ...query,
      joins: Array.from(new Set([...(query?.joins || []), "project"])),
    },
    controller,
    excludeInterceptor,
  });

// POs have no direct clientId column — client is inherited via
// purchaseOrder.quote.clientId (PO has no locationId/clientId of its
// own; see PurchaseOrder entity comment). The list endpoint already
// leftJoinAndSelects "quote", so the dot-path filter resolves against
// that existing join — no backend change needed.
const getClientPurchaseOrders = ({ query = {}, controller = null, excludeInterceptor = false }: any) =>
  getPurchaseOrders({
    query: {
      ...query,
      joins: Array.from(new Set([...(query?.joins || []), "provider"])),
    },
    controller,
    excludeInterceptor,
  });

/**
 * Right-aligned "View all X →" affordance used on every record tab's
 * SectionHeader. Uses theme-ui Link so the href is properly typed and
 * to keep the lint rule against @ts-ignore satisfied.
 */
function ViewAllLink({ href, label }: { href: string; label: string }) {
  return (
    <Link
      href={href}
      sx={{
        fontFamily: "Poppins",
        fontSize: "12px",
        fontWeight: 500,
        color: themevals.colors.info,
        textDecoration: "none",
        "&:hover": {
          color: themevals.colors.info,
          textDecoration: "underline",
        },
      }}
    >
      {label} →
    </Link>
  );
}

/**
 * GAP-51/56/62 (mockups 16-20) — plan-tag segment chips above a record
 * tab's table: All / Plan-tagged / One-off. Selecting a chip drives the
 * table's server-side filter on the given `filterKey` (which resolves to
 * the right table alias per tab — `servicePlanId` on the RFQ root,
 * `quoteRequest.servicePlanId` on the joined alias elsewhere), using the
 * `SQLfilter` null / notnull matchers via the `value:matcher` convention.
 *
 * Count model (deliberate, no new backend endpoint): the active chip
 * shows the live row `total` the table already returns for the current
 * filter. Showing all three counts at once would need separate
 * aggregate queries; the active-count approach is honest and free — the
 * number always matches exactly what the table is showing. The inactive
 * chips are unlabelled with a count until selected.
 */
function PlanSegmentChips({
  value,
  onChange,
  activeTotal,
}: {
  value: "all" | "tagged" | "oneoff";
  onChange: (v: "all" | "tagged" | "oneoff") => void;
  activeTotal: number | null;
}) {
  const chips: Array<{ key: "all" | "tagged" | "oneoff"; label: string }> = [
    { key: "all", label: "All" },
    { key: "tagged", label: "Plan-tagged" },
    { key: "oneoff", label: "One-off" },
  ];
  return (
    <Flex sx={{ gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
      {chips.map((chip) => {
        const active = chip.key === value;
        return (
          <Box
            key={chip.key}
            as="button"
            onClick={() => onChange(chip.key)}
            sx={{
              cursor: "pointer",
              fontFamily: "Poppins",
              fontSize: "12px",
              fontWeight: active ? 600 : 500,
              padding: "5px 12px",
              borderRadius: "999px",
              border: "1px solid",
              borderColor: active ? themevals.colors.black : themevals.colors.inputOutline,
              background: active ? themevals.colors.black : "white",
              color: active ? "white" : themevals.colors.dkgrey,
              transition: "all 120ms ease",
            }}
          >
            {chip.label}
            {active && activeTotal != null ? ` · ${activeTotal}` : ""}
          </Box>
        );
      })}
    </Flex>
  );
}

// GAP-51/56/62 — translate a segment selection into the TableData
// preFilters entry for a given servicePlanId column key. "all" clears
// the filter; "tagged" -> IS NOT NULL; "oneoff" -> IS NULL, using the
// SQLfilter `:matcher` convention (empty value, matcher only).
function segmentPreFilter(
  segment: "all" | "tagged" | "oneoff",
  filterKey: string,
): Record<string, string> {
  if (segment === "tagged") return { [filterKey]: ":notnull" };
  if (segment === "oneoff") return { [filterKey]: ":null" };
  return {};
}

function fmtCurrency(value: number): string {
  return Number(value ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/**
 * GAP-61 (mockup 19) — the four summary cards above the Invoices tab
 * table: Open balance / Past due / Rolling 90-day / Payment terms.
 * Reads entirely off the getClientOverview aggregate already fetched in
 * view.tsx — no new endpoint. Renders nothing until the overview loads
 * so it never flashes zeros. Same visual language as the Overview tab's
 * masthead cards (#F1EFE8 fill, Poppins).
 */
function InvoiceStatCards({ overview, client }: { overview: any; client: any }) {
  if (!overview?.healthCards) return null;
  const unpaid = overview.healthCards.unpaidInvoices ?? {};
  const billing = overview.billing?.rollingNinetyDay ?? {};

  const openBalance = Number(unpaid.totalDollars ?? 0);
  const openCount = Number(unpaid.count ?? 0);
  const pastDueDollars = Number(unpaid.pastDueDollars ?? 0);
  const pastDueCount = Number(unpaid.pastDueCount ?? 0);
  const mostOverdueDays = unpaid.mostOverdueDays;
  const invoiced90 = Number(billing.invoicedDollars ?? 0);
  const open90 = Number(billing.openDollars ?? 0);
  const paid90 = Math.max(0, invoiced90 - open90);
  // Payment terms: client.terms is net-days; mockup shows "Net-60" with
  // a per-plan override note. Defaults to Net 30 when unset (same rule
  // the Overview tab's billing snapshot uses).
  const terms = client?.terms ? `Net-${client.terms}` : "Net-30";

  const cards: Array<{ label: string; value: string; detail?: string; danger?: boolean }> = [
    {
      label: "Open balance",
      value: fmtCurrency(openBalance),
      detail: `${openCount} open invoice${openCount === 1 ? "" : "s"}`,
    },
    {
      label: "Past due",
      value: fmtCurrency(pastDueDollars),
      detail:
        pastDueCount > 0
          ? `${pastDueCount} invoice${pastDueCount === 1 ? "" : "s"}${
              mostOverdueDays ? ` · ${mostOverdueDays} days over` : ""
            }`
          : "nothing past due",
      danger: pastDueDollars > 0,
    },
    {
      label: "Rolling 90-day",
      value: fmtCurrency(invoiced90),
      detail: `${fmtCurrency(paid90)} paid`,
    },
    {
      label: "Payment terms",
      value: terms,
      detail: "plan may override for urgent WOs",
    },
  ];

  return (
    <Flex sx={{ gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
      {cards.map((c) => (
        <Box
          key={c.label}
          sx={{
            flex: "1 1 180px",
            minWidth: "170px",
            background: "#F1EFE8",
            borderRadius: "8px",
            padding: "14px 16px",
          }}
        >
          <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: themevals.colors.dkgrey }}>
            {c.label}
          </Box>
          <Box
            sx={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "20px",
              marginTop: "4px",
              color: c.danger ? "#633806" : themevals.colors.black,
            }}
          >
            {c.value}
          </Box>
          {c.detail && (
            <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: themevals.colors.dkgrey, marginTop: "2px" }}>
              {c.detail}
            </Box>
          )}
        </Box>
      ))}
    </Flex>
  );
}

/**
 * GAP-65 (mockup 20) — the summary cards above the POs tab table. Three
 * cards render off the getClientOverview `poSummary` aggregate: Open PO
 * commitment / Awaiting provider invoice / YTD PO spend. The mockup's
 * fourth card (Provider margin %) is a heavier cross-entity computation
 * (client-invoice revenue vs PO cost on COMPLETED work) and is
 * deliberately deferred — it is NOT rendered here rather than shown as
 * a fabricated/placeholder number. Same visual language as the other
 * stat rows.
 */
function PoStatCards({ overview }: { overview: any }) {
  const po = overview?.poSummary;
  if (!po) return null;

  const openDollars = Number(po.openCommitmentDollars ?? 0);
  const openCount = Number(po.openCommitmentCount ?? 0);
  const awaitingDollars = Number(po.awaitingInvoiceDollars ?? 0);
  const awaitingCount = Number(po.awaitingInvoiceCount ?? 0);
  const ytdDollars = Number(po.ytdSpendDollars ?? 0);
  const ytdCount = Number(po.ytdSpendCount ?? 0);
  const marginPct = po.marginPct;
  const marginProjects = Number(po.marginCompletedProjects ?? 0);

  const cards: Array<{ label: string; value: string; detail?: string; danger?: boolean }> = [
    {
      label: "Open PO commitment",
      value: fmtCurrency(openDollars),
      detail: `${openCount} open PO${openCount === 1 ? "" : "s"}`,
    },
    {
      label: "Awaiting provider invoice",
      value: fmtCurrency(awaitingDollars),
      detail:
        awaitingCount > 0
          ? `${awaitingCount} PO${awaitingCount === 1 ? "" : "s"} · accepted`
          : "none awaiting",
      danger: awaitingDollars > 0,
    },
    {
      label: "YTD PO spend",
      value: fmtCurrency(ytdDollars),
      detail: `${ytdCount} PO${ytdCount === 1 ? "" : "s"} this year`,
    },
    {
      // GAP-65 4th card — provider margin on completed work. Shows "—"
      // when there's no revenue basis (nothing completed/billed) rather
      // than a misleading 0% or 100%.
      label: "Provider margin",
      value: marginPct != null ? `${marginPct}%` : "—",
      detail:
        marginPct != null
          ? `on ${marginProjects} completed project${marginProjects === 1 ? "" : "s"}`
          : "no completed billed work yet",
    },
  ];

  return (
    <Flex sx={{ gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
      {cards.map((c) => (
        <Box
          key={c.label}
          sx={{
            flex: "1 1 180px",
            minWidth: "170px",
            background: "#F1EFE8",
            borderRadius: "8px",
            padding: "14px 16px",
          }}
        >
          <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: themevals.colors.dkgrey }}>
            {c.label}
          </Box>
          <Box
            sx={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "20px",
              marginTop: "4px",
              color: c.danger ? "#633806" : themevals.colors.black,
            }}
          >
            {c.value}
          </Box>
          {c.detail && (
            <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: themevals.colors.dkgrey, marginTop: "2px" }}>
              {c.detail}
            </Box>
          )}
        </Box>
      ))}
    </Flex>
  );
}

export default function Client() {
  const toast = useToast();
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useContext<any>(UserContext);
  const columnHelper = createColumnHelper();

  const [client, setClient] = useState<any>(null);
  // GAP-53 (mockups 16-20) — id -> plan name map for the record tabs'
  // source second-lines. The per-row payloads carry servicePlanId but
  // not the plan NAME; rather than join maintenance_plan through five
  // list query builders (servicePlanId isn't a mapped relation on most
  // of these entities), fetch the client's plans once (a client has a
  // handful) and resolve id -> name in the cell. Falls back to the
  // generic "Plan-tagged" label until the map loads or if an id isn't
  // found. One request, serves all five tabs.
  const [planNameById, setPlanNameById] = useState<Record<string, string>>({});
  // GAP-61 (mockup 19) — the Invoices tab's four summary cards (Open
  // balance / Past due / Rolling 90-day / Payment terms) read entirely
  // off the existing getClientOverview aggregate (healthCards +
  // billing). No new endpoint: three of the four cards were already
  // computed for the Overview tab; the past-due dollar split is one
  // extra conditional SUM added to the same query this session. Fetched
  // once here alongside the client so the cards render without their
  // own round trip when the tab opens.
  const [overview, setOverview] = useState<any>(null);
  // GAP-51/56/62 — per-tab plan-tag segment selection + the live total
  // for the active segment (read from TableData's returned `total`).
  // RFQ tab is wired first; the other tabs follow the same pattern once
  // this one is proven end to end.
  const [rfqSegment, setRfqSegment] = useState<"all" | "tagged" | "oneoff">("all");
  const [rfqSegmentTotal, setRfqSegmentTotal] = useState<number | null>(null);
  // Same segment state for the other four tabs. Filter key differs per
  // tab: RFQ is the native `servicePlanId`; Quotes/Invoices/POs reach it
  // via the joined `quoteRequest` alias; Projects via `originQuoteRequest`.
  const [quoteSegment, setQuoteSegment] = useState<"all" | "tagged" | "oneoff">("all");
  const [quoteSegmentTotal, setQuoteSegmentTotal] = useState<number | null>(null);
  const [projectSegment, setProjectSegment] = useState<"all" | "tagged" | "oneoff">("all");
  const [projectSegmentTotal, setProjectSegmentTotal] = useState<number | null>(null);
  const [invoiceSegment, setInvoiceSegment] = useState<"all" | "tagged" | "oneoff">("all");
  const [invoiceSegmentTotal, setInvoiceSegmentTotal] = useState<number | null>(null);
  const [poSegment, setPoSegment] = useState<"all" | "tagged" | "oneoff">("all");
  const [poSegmentTotal, setPoSegmentTotal] = useState<number | null>(null);
  const hasTrackedView = useRef(false);

  const initClient = async () => {
    const res = await getClient({ data: { id: params.id } });
    setClient(res?.id ? res : null);

    if (res?.id) {
      // Load this client's maintenance plans for the id -> name map.
      // getServicePlans is client-scoped server-side; we also pass the
      // clientId filter so a company viewer only pulls this client's
      // plans. Non-fatal: on failure the second-lines just show the
      // generic "Plan-tagged" label.
      void (async () => {
        try {
          const plansRes: any = await getServicePlans({
            query: { filters: { clientId: res.id }, recordsPer: "200" },
          });
          const list: any[] = Array.isArray(plansRes)
            ? plansRes
            : Array.isArray(plansRes?.items)
            ? plansRes.items
            : [];
          const map: Record<string, string> = {};
          for (const plan of list) {
            if (plan?.id && plan?.name) map[plan.id] = plan.name;
          }
          setPlanNameById(map);
        } catch {
          setPlanNameById({});
        }
      })();

      // GAP-61 — client overview aggregate for the Invoices-tab summary
      // cards. Non-fatal: on failure the cards just don't render.
      void (async () => {
        try {
          const ov: any = await getClientOverview(res.id);
          setOverview(ov ?? null);
        } catch {
          setOverview(null);
        }
      })();
    }

    if (res?.id && !hasTrackedView.current) {
      hasTrackedView.current = true;
      void trackActivity({
        module: "clients",
        action: "view_client_detail",
        actionType: "view",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "client",
        entityId: res.id,
        metadata: {
          clientName: res.name,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    }
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Client";
    hasTrackedView.current = false;
    initClient();
  }, [params.id]);

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const submitSync = async () => {
    setConfirmSubmit(true);
    const res = await resyncClient({ data: client });
    if (res?.success) {
      initClient();
      toast.add("Client synced.", "var(--theme-ui-colors-success)");
    } else {
      toast.add(res?.message ? res.message : "Error syncing client.", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
  };

  const confirmDelete = async () => {
    setConfirmSubmit(true);
    const res = await deleteClient({ data: client });
    if (res?.success) {
      toast.add("Client deactivated.", "var(--theme-ui-colors-success)");
      navigate("/clients");
    } else {
      toast.add(res?.message ? res.message : "Error deleting client.", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };

  const submitDelete = async () => {
    setConfirmModal(
      <BasicModal
        title="Deactivate client?"
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <Flex sx={{ gap: "10px" }}>
            <BasicButton
              type="button"
              scheme="primary"
              outline={true}
              onClick={() => setConfirmModal(false)}
              $submitting={confirmSubmit}
              $iconHeight="14px"
              $iconWidth="14px"
            >
              {"No, cancel"}
            </BasicButton>
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => confirmDelete()}
              $submitting={confirmSubmit}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#000"
              keepText={true}
            >
              {!confirmSubmit && <TrashIcon fill="#fff" />}
              {"Deactivate"}
            </BasicButton>
          </Flex>
        }
      >
        <P>
          Are you sure you want to deactivate <strong style={{ color: "var(--theme-ui-colors-black)" }}>{client?.name}</strong>?
        </P>
      </BasicModal>,
    );
  };

  const formatLastSync = (iso?: string) => {
    if (!iso) return "N/A";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "N/A";
    const month = d.toLocaleString("en-US", { month: "short" });
    const day = d.getDate();
    const year = d.getFullYear();
    const ordinal = (n: number) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${month} ${day}${ordinal(day)}, ${year} ${hours}:${minutes} ${ampm}`;
  };

  const lastSyncSuccessful = client?.external?.zoho?.lastSyncSuccessful;
  const lastSyncColor =
    lastSyncSuccessful === undefined ? themevals.colors.dkgrey : lastSyncSuccessful ? themevals.colors.success : themevals.colors.danger;
  const lastSyncText = client?.external?.zoho?.lastSync ? formatLastSync(client.external.zoho.lastSync) : "N/A";

  // GAP-52/53 — shared source second-line for the record tabs. Resolves
  // the plan id to its name when known (mockups 16-20 show the plan
  // name on plan-tagged rows), falls back to the generic "Plan-tagged"
  // label while the name map is still loading or if the id isn't in it,
  // and "One-off" when there's no plan id at all.
  const planSourceLine = (servicePlanId?: string | null) => {
    const label = servicePlanId
      ? planNameById[servicePlanId] || "Plan-tagged"
      : "One-off";
    return (
      <Box
        sx={{ fontSize: "11px", color: themevals.colors.dkgrey, fontFamily: "Poppins", marginTop: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "130px" }}
        title={servicePlanId && planNameById[servicePlanId] ? planNameById[servicePlanId] : undefined}
      >
        {label}
      </Box>
    );
  };

  const rfqColumns = [
    columnHelper.accessor("sequence", {
      id: "sequence",
      cell: (info: any) => (
        <Box sx={{ cursor: "pointer" }} onClick={() => navigate(`/quote-requests/${info.row.original.id}`)}>
          <Box sx={{ fontWeight: 500, color: "#000" }}>
            {`GMR-${String(info.getValue()).padStart(5, "0")}`}
          </Box>
          {/* GAP-52/53 (mockup 16) — source second-line. `servicePlanId`
           * is native on quote_request; planSourceLine resolves it to
           * the plan name (or the generic "Plan-tagged" fallback). */}
          {planSourceLine(info.row.original?.servicePlanId)}
        </Box>
      ),
      header: () => <span>RFQ #</span>,
      meta: { style: { width: "130px" } },
    }),
    columnHelper.accessor((row: any) => row?.projectName ?? "Instant Estimate", {
      id: "projectName",
      cell: (info: any) => <Span color="#000" fontWeight="500">{info.getValue()}</Span>,
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => {
        switch (info.getValue()) {
          case 1:
            return <Badge as="span" variant="warning">New</Badge>;
          case 2:
            return <Badge as="span" variant="success">Quoted</Badge>;
          case 5:
            return <Badge as="span" variant="danger">Cancelled</Badge>;
          case 6:
            return <Badge as="span" variant="orange">Expired</Badge>;
          default:
            return <Badge as="span" variant="info">Estimated</Badge>;
        }
      },
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor((row: any) => row?.serviceDate, {
      id: "serviceDate",
      // Calendar date: shown as picked (helpers/dateOnly), not shifted a day by the browser zone.
      cell: (info: any) => formatDateOnly(info.getValue()),
      header: () => <span>Service Date</span>,
    }),
    columnHelper.accessor((row: any) => row?.updatedAt, {
      id: "updatedAt",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Last Updated</span>,
    }),
  ];

  const quoteStatusBadge = (status: number) => {
    switch (status) {
      case 1:
        return <Badge as="span" variant="success">Accepted</Badge>;
      case 2:
        return <Badge as="span" variant="danger">Denied</Badge>;
      case 3:
        return <Badge as="span" variant="info">Draft</Badge>;
      case 4:
        return <Badge as="span" variant="orange">Expired</Badge>;
      case 5:
        return <Badge as="span" variant="info">Superseded</Badge>;
      case 6:
        return <Badge as="span" variant="info">Price Locked</Badge>;
      default:
        return <Badge as="span" variant="warning">New</Badge>;
    }
  };

  const quoteColumns = [
    columnHelper.accessor("sequence", {
      id: "sequence",
      cell: (info: any) => (
        <Box sx={{ cursor: "pointer" }} onClick={() => navigate(`/quotes/${info.row.original.id}`)}>
          <Box sx={{ fontWeight: 500, color: "#000" }}>
            {`GMQ-${String(info.getValue()).padStart(5, "0")}`}
          </Box>
          {/* GAP-52/53 (mockup 17) — source second-line. Quote inherits
           * the tag via quote.quoteRequest.servicePlanId; resolved to
           * the plan name by planSourceLine. */}
          {planSourceLine(info.row.original?.quoteRequest?.servicePlanId)}
        </Box>
      ),
      header: () => <span>Quote #</span>,
      meta: { style: { width: "130px" } },
    }),
    columnHelper.accessor((row: any) => row?.projectName, {
      id: "projectName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => quoteStatusBadge(info.getValue()),
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor((row: any) => row?.total, {
      id: "total",
      cell: (info: any) => info.getValue()?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? "$0.00",
      header: () => <span>Total</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.updatedAt, {
      id: "updatedAt",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Last Updated</span>,
    }),
  ];

  const projectColumns = [
    columnHelper.accessor("sequence", {
      id: "sequence",
      cell: (info: any) => (
        <Box sx={{ cursor: "pointer" }} onClick={() => navigate(`/projects/${info.row.original.id}`)}>
          <Box sx={{ fontWeight: 500, color: "#000" }}>
            {`GMP-${String(info.getValue()).padStart(5, "0")}`}
          </Box>
          {/* GAP-52/53 (mockup 18) — source second-line. Project
           * inherits the tag via originQuoteRequest.servicePlanId;
           * resolved to the plan name by planSourceLine. */}
          {planSourceLine(info.row.original?.originQuoteRequest?.servicePlanId)}
        </Box>
      ),
      header: () => <span>Project #</span>,
      meta: { style: { width: "130px" } },
    }),
    columnHelper.accessor((row: any) => row?.projectName, {
      id: "projectName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => {
        const status = info.getValue();
        return <Badge as="span" variant={status?.color || "info"}>{status?.name || "—"}</Badge>;
      },
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor((row: any) => row?.serviceDate, {
      id: "serviceDate",
      // Calendar date: shown as picked (helpers/dateOnly), not shifted a day by the browser zone.
      cell: (info: any) => formatDateOnly(info.getValue()),
      header: () => <span>Service Date</span>,
    }),
  ];

  const invoiceColumns = [
    columnHelper.accessor("sequence", {
      id: "sequence",
      cell: (info: any) => (
        <Box sx={{ cursor: "pointer" }} onClick={() => navigate(`/invoices/${info.row.original.id}`)}>
          <Box sx={{ fontWeight: 500, color: "#000" }}>
            {`GMI-${String(info.getValue()).padStart(5, "0")}`}
          </Box>
          {/* GAP-52/53 (mockup 19) — source second-line. Invoice
           * inherits the tag via quote.quoteRequest.servicePlanId;
           * resolved to the plan name by planSourceLine. */}
          {planSourceLine(info.row.original?.quote?.quoteRequest?.servicePlanId)}
        </Box>
      ),
      header: () => <span>Invoice #</span>,
      meta: { style: { width: "130px" } },
    }),
    columnHelper.accessor((row: any) => row?.project?.projectName, {
      id: "project.projectName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Project</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => <InvoiceStatusBadge invoice={info.row.original} />,
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor((row: any) => row?.total, {
      id: "total",
      cell: (info: any) => info.getValue()?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? "$0.00",
      header: () => <span>Total</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.dueDate, {
      id: "dueDate",
      cell: (info: any) => {
        // GAP-64 (mockup 19) — Age / due dual-line qualifier. Line 1 is
        // the relative age; line 2 is the contextual due/paid state. The
        // mockup shows `12d past due` / `was due Jul 10`, `14d` / `due
        // Sep 05`, `28d` / `paid Jul 18`. Paid date isn't on the list
        // payload, so a paid invoice shows `paid` without a fabricated
        // date rather than inventing one from updatedAt.
        const row = info.row.original;
        const status = row?.status;
        const dueDate = row?.dueDate ? moment(row.dueDate) : null;
        const createdAt = row?.createdAt ? moment(row.createdAt) : null;
        const now = moment();

        // Age line: days since created (or the mockup's short forms).
        let ageLine = "";
        if (createdAt) {
          const days = now.diff(createdAt, "days");
          const hours = now.diff(createdAt, "hours");
          ageLine = days >= 1 ? `${days}d` : hours >= 1 ? `${hours}h` : "today";
        }

        // State line depends on status + dueDate.
        let stateLine: React.ReactNode = null;
        if (status === 2) {
          stateLine = <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey, fontFamily: "Poppins" }}>paid</Box>;
        } else if (status === 4) {
          stateLine = <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey, fontFamily: "Poppins" }}>voided</Box>;
        } else if (dueDate) {
          const overdue = dueDate.isBefore(now, "day");
          if (overdue) {
            const daysOver = now.diff(dueDate, "days");
            // Overdue: line 1 becomes the urgency signal, line 2 the date.
            return (
              <Box>
                <Box sx={{ color: "#791F1F", fontWeight: 500 }}>{daysOver}d past due</Box>
                <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey, fontFamily: "Poppins", marginTop: "2px" }}>
                  was due {dueDate.format("MMM D")}
                </Box>
              </Box>
            );
          }
          stateLine = (
            <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey, fontFamily: "Poppins" }}>
              due {dueDate.format("MMM D")}
            </Box>
          );
        } else {
          stateLine = <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey, fontFamily: "Poppins" }}>draft</Box>;
        }

        return (
          <Box>
            <Box>{ageLine}</Box>
            <Box sx={{ marginTop: "2px" }}>{stateLine}</Box>
          </Box>
        );
      },
      header: () => <span>Age / due</span>,
    }),
  ];

  const poColumns = [
    columnHelper.accessor("sequence", {
      id: "sequence",
      cell: (info: any) => (
        <Box sx={{ cursor: "pointer" }} onClick={() => navigate(`/purchase-orders/${info.row.original.id}`)}>
          <Box sx={{ fontWeight: 500, color: "#000" }}>
            {`GMO-${String(info.getValue()).padStart(5, "0")}`}
          </Box>
          {/* GAP-52/53 (mockup 20) — source second-line. PO inherits the
           * tag via quote.quoteRequest.servicePlanId; resolved to the
           * plan name by planSourceLine. */}
          {planSourceLine(info.row.original?.quote?.quoteRequest?.servicePlanId)}
        </Box>
      ),
      header: () => <span>PO #</span>,
      meta: { style: { width: "130px" } },
    }),
    columnHelper.accessor((row: any) => row?.project?.projectName ?? "Instant Estimate", {
      id: "project.projectName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Project</span>,
    }),
    columnHelper.accessor((row: any) => row?.provider?.name, {
      id: "provider.name",
      cell: (info: any) => {
        // GAP-66 (mockup 20) — provider on line 1, depot on line 2
        // (`Southeast Bulk` / `Alpharetta depot`). Depot comes from the
        // first line item's depot (a PO is placed against one depot;
        // same assumption the PDF generator makes). Falls back cleanly
        // when a PO's items carry no depot.
        const row = info.row.original;
        const depotName =
          Array.isArray(row?.lineitems) && row.lineitems.length > 0
            ? row.lineitems.find((li: any) => li?.depot?.name)?.depot?.name
            : null;
        return (
          <Box>
            <Box>{info.getValue()}</Box>
            {depotName && (
              <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey, fontFamily: "Poppins", marginTop: "2px" }}>
                {depotName}
              </Box>
            )}
          </Box>
        );
      },
      header: () => <span>Provider</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => {
        switch (info.getValue()) {
          case 0:
            return <Badge as="span" variant="warning">New</Badge>;
          case 1:
            return <Badge as="span" variant="info">Sent</Badge>;
          case 2:
            return <Badge as="span" variant="success">Accepted</Badge>;
          case 3:
            return <Badge as="span" variant="warning">Billed</Badge>;
          case 4:
            return <Badge as="span" variant="danger">Denied</Badge>;
          case 5:
            return <Badge as="span" variant="success">Closed</Badge>;
          case 6:
            return <Badge as="span" variant="danger">Cancelled</Badge>;
          default:
            return <Badge as="span" variant="warning">New</Badge>;
        }
      },
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor((row: any) => row?.poTotal, {
      id: "total",
      cell: (info: any) => info.getValue()?.toLocaleString("en-US", { style: "currency", currency: "USD" }) ?? "$0.00",
      header: () => <span>Total</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.createdAt, {
      id: "createdAt",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>PO Date</span>,
    }),
  ];

  const tabLinks = [
    { key: "overview", label: "Overview" },
    { key: "contacts", label: "Contacts" },
    { key: "plans", label: "Plans" },
    { key: "rfqs", label: "RFQs" },
    { key: "quotes", label: "Quotes" },
    { key: "projects", label: "Projects" },
    { key: "invoices", label: "Invoices" },
    { key: "pos", label: "POs" },
    { key: "notes", label: "Notes" },
  ];

  if (!client) return <Box sx={{ textAlign: "center", paddingY: "1.5rem" }}><Spinner /></Box>;

  const tabBodySx = {
    paddingTop: "24px",
    paddingLeft: "32px",
    paddingRight: "32px",
    paddingBottom: "24px",
  };

  return (
    <>
      <Header></Header>
      <Page backAction={<Link variant="back" href="/clients"><BackArrowIcon />Back to Clients</Link>}>
        <Flex sx={{ gap: "20px", alignItems: "center", marginBottom: "10px" }}>
          <H2>{client.name}</H2>
          {/* F3b: consistent "Edit" button (matching the bordered Edit
              action on Project / PO records) instead of a bare pencil
              icon. This view has no left rail, so it sits inline beside
              the client name. */}
          <Link
            href={`/clients/${client.id}/edit`}
            sx={{
              display: "flex", alignItems: "center", gap: "7px",
              padding: "7px 14px",
              border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px",
              textDecoration: "none", color: themevals.colors.black, backgroundColor: "#fff",
              fontFamily: "Poppins", fontSize: "13px", fontWeight: 500, cursor: "pointer",
              "&:hover": { backgroundColor: "#F7F8FA", borderColor: themevals.colors.dkgrey },
            }}
          >
            <PencilIcon height="13px" fill="currentColor" />
            Edit
          </Link>
        </Flex>

        <Tabs links={tabLinks}>
          <Tab itemKey="overview">
            <Box sx={tabBodySx}>
              <OverviewTab clientId={client.id} client={client} />

              {/* Account actions — Resync + Deactivate preserved from the
                  pre-rebuild Overview tab. Basic Info / Address / Emails
                  are redundant now (editable via the pencil icon above,
                  and address is covered by the new HQ address card), so
                  only the two real actions carry over here. */}
              <Box sx={{ marginTop: "32px", borderTop: "1px solid", borderColor: "inputOutline", paddingTop: "20px" }}>
                <Flex sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "20px" }}>
                  <Flex sx={{ alignItems: "center", gap: "16px" }}>
                    <Box>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Third-Party Sync</Label>
                      <P fontFamily={"Poppins"} color={lastSyncColor} fontSize={"14px"} fontWeight={600} marginTop={"5px"} marginBottom={"0px"}>
                        {lastSyncText}
                      </P>
                    </Box>
                    <BasicButton
                      scheme="secondaryOutline"
                      type="button"
                      $iconHeight="14px"
                      $iconWidth="14px"
                      $loadingColor="#000"
                      $submitting={confirmSubmit}
                      keepText={true}
                      onClick={() => submitSync()}
                    >
                      {!confirmSubmit && <ReloadIcon fill={"#000"} />}Resync
                    </BasicButton>
                  </Flex>
                  <BasicButton scheme="dangerSecondary" type="button" onClick={() => submitDelete()}>
                    <TrashIcon fill={"#A60000"} /> Deactivate
                  </BasicButton>
                </Flex>
              </Box>
            </Box>
          </Tab>

          <Tab itemKey="contacts">
            <Box sx={tabBodySx}>
              <ContactsTab clientId={client.id} />
            </Box>
          </Tab>

          <Tab itemKey="plans">
            <Box sx={tabBodySx}>
              <PlansTab clientId={client.id} />
            </Box>
          </Tab>

          <Tab itemKey="rfqs">
            <Box sx={tabBodySx}>
              <TabSection
                title="RFQs"
                subtitle="requests for this client, most recent first"
                action={<ViewAllLink href="/quote-requests" label="View all RFQs" />}
              >
                {/* GAP-51/56/62 — plan-tag segment chips. `servicePlanId`
                 * is native on the quote_request root alias, so the
                 * filter key is the bare column. */}
                <PlanSegmentChips
                  value={rfqSegment}
                  onChange={setRfqSegment}
                  activeTotal={rfqSegmentTotal}
                />
                <TableData
                  api={getQuoteRequests}
                  columns={rfqColumns}
                  defaultSortBy="updatedAt"
                  defaultSortDir="desc"
                  nothingFoundText="No RFQs found for this client."
                  defaultFilters={{ clientId: client.id }}
                  preFilters={segmentPreFilter(rfqSegment, "servicePlanId")}
                  returnData={(res: any) => setRfqSegmentTotal(typeof res?.total === "number" ? res.total : null)}
                  stateKey={`client-${client.id}-rfqs`}
                />
              </TabSection>
            </Box>
          </Tab>

          <Tab itemKey="quotes">
            <Box sx={tabBodySx}>
              <TabSection
                title="Quotes"
                subtitle="quotes sent to this client"
                action={<ViewAllLink href="/quotes" label="View all quotes" />}
              >
                {/* GAP-51/56/62 — quote inherits the tag via the joined
                 * quoteRequest alias. */}
                <PlanSegmentChips
                  value={quoteSegment}
                  onChange={setQuoteSegment}
                  activeTotal={quoteSegmentTotal}
                />
                <TableData
                  api={getQuotes}
                  columns={quoteColumns}
                  defaultSortBy="updatedAt"
                  defaultSortDir="desc"
                  nothingFoundText="No Quotes found for this client."
                  defaultFilters={{ clientId: client.id }}
                  preFilters={segmentPreFilter(quoteSegment, "quoteRequest.servicePlanId")}
                  returnData={(res: any) => setQuoteSegmentTotal(typeof res?.total === "number" ? res.total : null)}
                  stateKey={`client-${client.id}-quotes`}
                />
              </TabSection>
            </Box>
          </Tab>

          <Tab itemKey="projects">
            <Box sx={tabBodySx}>
              <TabSection
                title="Projects"
                subtitle="scheduled and completed work for this client"
                action={<ViewAllLink href="/projects" label="View all projects" />}
              >
                {/* GAP-51/56/62 — project inherits the tag via the joined
                 * originQuoteRequest alias. */}
                <PlanSegmentChips
                  value={projectSegment}
                  onChange={setProjectSegment}
                  activeTotal={projectSegmentTotal}
                />
                <TableData
                  api={getProjects}
                  columns={projectColumns}
                  defaultSortBy="createdAt"
                  defaultSortDir="desc"
                  nothingFoundText="No Projects found for this client."
                  defaultFilters={{ clientId: client.id }}
                  preFilters={segmentPreFilter(projectSegment, "originQuoteRequest.servicePlanId")}
                  returnData={(res: any) => setProjectSegmentTotal(typeof res?.total === "number" ? res.total : null)}
                  stateKey={`client-${client.id}-projects`}
                />
              </TabSection>
            </Box>
          </Tab>

          <Tab itemKey="invoices">
            <Box sx={tabBodySx}>
              <TabSection
                title="Invoices"
                subtitle="billing history for this client"
                action={<ViewAllLink href="/invoices" label="View all invoices" />}
              >
                {/* GAP-61 (mockup 19) — Open balance / Past due /
                 * Rolling 90-day / Payment terms summary cards, off the
                 * getClientOverview aggregate. */}
                <InvoiceStatCards overview={overview} client={client} />
                {/* GAP-51/56/62 — invoice inherits the tag via the joined
                 * quoteRequest alias (invoice findAll now joins it). */}
                <PlanSegmentChips
                  value={invoiceSegment}
                  onChange={setInvoiceSegment}
                  activeTotal={invoiceSegmentTotal}
                />
                <TableData
                  api={getClientInvoices}
                  columns={invoiceColumns}
                  defaultSortBy="sequence"
                  defaultSortDir="desc"
                  nothingFoundText="No Invoices found for this client."
                  defaultFilters={{ clientId: client.id }}
                  preFilters={segmentPreFilter(invoiceSegment, "quoteRequest.servicePlanId")}
                  returnData={(res: any) => setInvoiceSegmentTotal(typeof res?.total === "number" ? res.total : null)}
                  stateKey={`client-${client.id}-invoices`}
                />
              </TabSection>
            </Box>
          </Tab>

          <Tab itemKey="pos">
            <Box sx={tabBodySx}>
              <TabSection
                title="Purchase Orders"
                subtitle="provider-side commitments for this client's projects"
                action={<ViewAllLink href="/purchase-orders" label="View all POs" />}
              >
                {/* GAP-65 (mockup 20) — Open commitment / Awaiting
                 * provider invoice / YTD spend cards, off the
                 * getClientOverview poSummary aggregate. Provider margin
                 * card deferred. */}
                <PoStatCards overview={overview} />
                {/* GAP-51/56/62 — PO inherits the tag via the joined
                 * quoteRequest alias (PO findAll now joins it). */}
                <PlanSegmentChips
                  value={poSegment}
                  onChange={setPoSegment}
                  activeTotal={poSegmentTotal}
                />
                <TableData
                  api={getClientPurchaseOrders}
                  columns={poColumns}
                  defaultSortBy="sequence"
                  defaultSortDir="desc"
                  nothingFoundText="No Purchase Orders found for this client."
                  defaultFilters={{ "quote.clientId": client.id }}
                  preFilters={segmentPreFilter(poSegment, "quoteRequest.servicePlanId")}
                  returnData={(res: any) => setPoSegmentTotal(typeof res?.total === "number" ? res.total : null)}
                  stateKey={`client-${client.id}-pos`}
                />
              </TabSection>
            </Box>
          </Tab>

          <Tab itemKey="notes">
            <Box sx={tabBodySx}>
              <NotesTab clientId={client.id} />
            </Box>
          </Tab>
        </Tabs>
      </Page>
      {confirmModal}
    </>
  );
}
